import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Edit3, X } from "lucide-react";
import { toast } from "sonner";
import { Field } from "./shared";

const AUDIENCE_OPTIONS = [
  { v: "all", l: "All Confirmed Subscribers", hint: "Everyone in the newsletter (default)." },
  { v: "paid_members", l: "Paid Members Only", hint: "Active $20/mo Inner Circle subscribers." },
  { v: "free_members", l: "Free Members Only", hint: "Free fan_members tier — typically loyal fan-mail senders." },
  { v: "all_members", l: "All Fan Members", hint: "Free + Paid fan_members combined." },
  { v: "subscribers_only", l: "Subscribers (Not Members)", hint: "Newsletter readers who haven't joined the Fan Club yet — good upsell target." },
];

export function BlastsPanel() {
  const blank = {
    title: "",
    subject: "",
    body: "",
    type_label: "Inner Circle Drop",
    cta_label: "",
    cta_url: "",
    image_url: "",
    scheduled_for: "",
    audience: "all",
  };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [audienceCounts, setAudienceCounts] = useState({});
  const [digestBusy, setDigestBusy] = useState(false);
  const [scheduleOn, setScheduleOn] = useState(false);
  const [scheduleDay, setScheduleDay] = useState(4);
  const [scheduleHour, setScheduleHour] = useState(10);
  const [preview, setPreview] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);

  const load = async () => {
    try {
      const [list, counts, sched] = await Promise.all([
        api.get("/admin/announcements"),
        api.get("/admin/announcements/audience-counts").catch(() => ({ data: { counts: {} } })),
        api.get("/admin/announcements/digest-schedule").catch(() => ({ data: { enabled: false } })),
      ]);
      setItems(list.data.announcements || []);
      setAudienceCounts(counts.data.counts || {});
      setScheduleOn(!!sched.data.enabled);
      if (sched.data.day_of_week != null) setScheduleDay(sched.data.day_of_week);
      if (sched.data.hour != null) setScheduleHour(sched.data.hour);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not load blasts");
    }
  };
  useEffect(() => {
    const id = setTimeout(load, 0);
    return () => clearTimeout(id);
  }, []);

  const reset = () => {
    setForm(blank);
    setEditingId(null);
    setShowForm(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        subject: form.subject || form.title,
        scheduled_for: form.scheduled_for
          ? new Date(form.scheduled_for).toISOString()
          : null,
      };
      if (editingId) {
        await api.put(`/admin/announcements/${editingId}`, payload);
        toast.success("Blast updated.");
      } else {
        await api.post("/admin/announcements", payload);
        toast.success(
          payload.scheduled_for
            ? `Scheduled for ${new Date(payload.scheduled_for).toLocaleString()}`
            : "Saved as draft."
        );
      }
      reset();
      await load();
    } catch (err) {
      toast.error(formatApiError(err) || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const sendNow = async (id, title, audience) => {
    const audCount = audienceCounts[audience || "all"] ?? "?";
    const audLabel = AUDIENCE_OPTIONS.find((x) => x.v === (audience || "all"))?.l || "all subscribers";
    if (!window.confirm(`Send "${title}" to ${audCount} recipient(s) (${audLabel}) RIGHT NOW? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/announcements/${id}/send-now`);
      if (data.already_sent) {
        toast.message("Already sent.");
      } else {
        toast.success(`Sent to ${data.stats.sent} subscriber${data.stats.sent === 1 ? "" : "s"}.`);
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err) || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  const removeAnn = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await api.delete(`/admin/announcements/${id}`);
      toast.success("Deleted.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err) || "Delete failed");
    }
  };

  const sendDigest = async () => {
    const audCount = audienceCounts["all"] ?? "?";
    if (
      !window.confirm(
        `Email the Latest-Drop digest (your newest music + blog + Chronicle) to ${audCount} confirmed subscriber(s) RIGHT NOW? This cannot be undone.`
      )
    )
      return;
    setDigestBusy(true);
    try {
      const { data } = await api.post("/admin/announcements/send-digest");
      toast.success(
        `Digest sent to ${data.stats.sent} subscriber${data.stats.sent === 1 ? "" : "s"}. Featured: ${(data.featured || []).join(", ")}`
      );
      await load();
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) || "Digest send failed"
      );
    } finally {
      setDigestBusy(false);
    }
  };

  const openPreview = async () => {
    setPreviewBusy(true);
    try {
      const { data } = await api.get("/admin/announcements/digest-preview");
      if (!data.ok) {
        toast.error("No published content to feature yet.");
        return;
      }
      setPreview(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not build preview");
    } finally {
      setPreviewBusy(false);
    }
  };

  const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const HOURS = Array.from({ length: 24 }, (_, h) => {
    const ampm = h < 12 ? "AM" : "PM";
    const h12 = h % 12 || 12;
    return { value: h, label: `${h12}:00 ${ampm} ET` };
  });

  const saveSchedule = async (patch = {}) => {
    const body = {
      enabled: patch.enabled ?? scheduleOn,
      day_of_week: patch.day_of_week ?? scheduleDay,
      hour: patch.hour ?? scheduleHour,
    };
    const prev = { enabled: scheduleOn, day_of_week: scheduleDay, hour: scheduleHour };
    setScheduleOn(body.enabled);
    setScheduleDay(body.day_of_week);
    setScheduleHour(body.hour);
    try {
      const { data } = await api.put("/admin/announcements/digest-schedule", body);
      toast.success(
        body.enabled
          ? `Auto-digest ON — sends every ${data.label} to confirmed subscribers.`
          : "Auto-digest turned off."
      );
    } catch (err) {
      setScheduleOn(prev.enabled);
      setScheduleDay(prev.day_of_week);
      setScheduleHour(prev.hour);
      toast.error(formatApiError(err) || "Could not update schedule");
    }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setForm({
      title: a.title || "",
      subject: a.subject || "",
      body: a.body || "",
      type_label: a.type_label || "Inner Circle Drop",
      cta_label: a.cta_label || "",
      cta_url: a.cta_url || "",
      image_url: a.image_url || "",
      scheduled_for: a.scheduled_for ? new Date(a.scheduled_for).toISOString().slice(0, 16) : "",
      audience: a.audience || "all",
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const statusColors = {
    sent: { color: "var(--gold)", label: "Sent" },
    scheduled: { color: "rgb(125, 211, 252)", label: "Scheduled" },
    draft: { color: "rgba(255,255,255,0.5)", label: "Draft" },
    sending: { color: "rgb(252, 211, 77)", label: "Sending…" },
    failed: { color: "rgb(248, 113, 113)", label: "Failed" },
  };

  return (
    <div className="space-y-8" data-testid="admin-blasts-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="eyebrow mb-1">Inner Circle Blasts</p>
          <h3 className="font-serif text-2xl lg:text-3xl tracking-tighter font-light">
            Compose, schedule, send
          </h3>
          <p className="text-[11px] text-white/45 mt-2 leading-relaxed max-w-xl">
            Scheduled blasts fire automatically on time. Confirmed Inner Circle
            subscribers only. Unsubscribe footer + CAN-SPAM compliance baked in.
          </p>
        </div>
        <Button
          onClick={() => (showForm ? reset() : setShowForm(true))}
          className="btn-gold rounded-none h-10 px-5 text-xs"
          data-testid="blasts-new-toggle"
        >
          {showForm ? <X size={14} /> : <Plus size={14} />}
          {showForm ? "Cancel" : "New Blast"}
        </Button>
      </div>

      <div
        className="surface p-5 lg:p-6 flex flex-col gap-4"
        style={{
          background:
            "linear-gradient(120deg, rgba(232,180,184,0.10), rgba(74,14,62,0.35))",
        }}
        data-testid="digest-card"
      >
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <p className="eyebrow mb-1" style={{ color: "var(--gold)" }}>
              One-Tap · Weekly Digest
            </p>
            <p className="text-sm text-white/70 leading-relaxed max-w-xl">
              Instantly email confirmed subscribers a combined digest of your
              newest music release, blog post, and Chronicle — no composing
              required.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Button
              onClick={openPreview}
              disabled={previewBusy}
              className="btn-ghost-gold rounded-none h-11 px-5 text-xs whitespace-nowrap"
              data-testid="preview-digest-btn"
            >
              {previewBusy ? "Building…" : "Preview"}
            </Button>
            <Button
              onClick={sendDigest}
              disabled={digestBusy}
              className="btn-gold rounded-none h-11 px-6 text-xs whitespace-nowrap"
              data-testid="send-digest-btn"
            >
              {digestBusy ? "Sending…" : "Send Latest-Drop Digest"}
            </Button>
          </div>
        </div>
        <div
          className="flex flex-col sm:flex-row sm:items-center gap-4 pt-4 border-t border-white/10"
          data-testid="digest-schedule-toggle"
        >
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={scheduleOn}
              onChange={(e) => saveSchedule({ enabled: e.target.checked })}
              className="accent-[color:var(--gold)] w-4 h-4"
              data-testid="digest-schedule-checkbox"
            />
            <span className="text-xs text-white/65 whitespace-nowrap">
              Auto-send every
            </span>
          </label>
          <div className="flex items-center gap-2">
            <select
              value={scheduleDay}
              onChange={(e) => saveSchedule({ day_of_week: Number(e.target.value) })}
              className="bg-black/40 border border-white/15 text-white text-xs rounded-none h-9 px-3 focus:border-[color:var(--gold)] outline-none"
              data-testid="digest-schedule-day"
            >
              {DAYS.map((d, i) => (
                <option key={i} value={i} className="bg-[#140912]">
                  {d}
                </option>
              ))}
            </select>
            <span className="text-xs text-white/40">at</span>
            <select
              value={scheduleHour}
              onChange={(e) => saveSchedule({ hour: Number(e.target.value) })}
              className="bg-black/40 border border-white/15 text-white text-xs rounded-none h-9 px-3 focus:border-[color:var(--gold)] outline-none"
              data-testid="digest-schedule-hour"
            >
              {HOURS.map((h) => (
                <option key={h.value} value={h.value} className="bg-[#140912]">
                  {h.label}
                </option>
              ))}
            </select>
          </div>
          <span className="text-[11px] text-white/40 italic">
            {scheduleOn ? "Active — I don't have to remember." : "Off"}
          </span>
        </div>
      </div>

      {preview && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          style={{ background: "rgba(5,3,8,0.85)", backdropFilter: "blur(6px)" }}
          data-testid="digest-preview-modal"
          onClick={() => setPreview(null)}
        >
          <div
            className="surface w-full max-w-[680px] max-h-[88vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div>
                <p className="eyebrow" style={{ color: "var(--gold)" }}>
                  Digest Preview
                </p>
                <p className="text-xs text-white/55 mt-1">
                  Goes to {preview.recipient_count} confirmed subscriber
                  {preview.recipient_count === 1 ? "" : "s"} · Featuring:{" "}
                  {(preview.featured || []).join(", ")}
                </p>
              </div>
              <button
                onClick={() => setPreview(null)}
                className="text-white/60 hover:text-white"
                data-testid="digest-preview-close"
              >
                <X size={18} />
              </button>
            </div>
            <iframe
              title="Digest preview"
              srcDoc={preview.html}
              className="flex-1 w-full bg-white"
              style={{ minHeight: "50vh", border: 0 }}
              data-testid="digest-preview-iframe"
            />
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="surface p-6 lg:p-8 space-y-4" data-testid="blasts-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Title">
              <Input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="bg-transparent border-0 border-b border-white/15 rounded-none h-11 px-0 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
                data-testid="blast-title"
              />
            </Field>
            <Field label="Subject Line (defaults to title)">
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="bg-transparent border-0 border-b border-white/15 rounded-none h-11 px-0 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
                data-testid="blast-subject"
              />
            </Field>
            <Field label="Type / Label (eyebrow)">
              <Input
                value={form.type_label}
                onChange={(e) => setForm({ ...form, type_label: e.target.value })}
                className="bg-transparent border-0 border-b border-white/15 rounded-none h-11 px-0 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
                data-testid="blast-type-label"
                placeholder="e.g. New Release · Pre-Order"
              />
            </Field>
            <Field label="Schedule For (leave empty = save as draft)">
              <Input
                type="datetime-local"
                value={form.scheduled_for}
                onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })}
                className="bg-transparent border-0 border-b border-white/15 rounded-none h-11 px-0 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
                data-testid="blast-scheduled-for"
              />
            </Field>
          </div>
          <Field label={`Audience · ${audienceCounts[form.audience] ?? "?"} recipient(s)`} full>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="blast-audience-picker">
              {AUDIENCE_OPTIONS.map((opt) => {
                const active = form.audience === opt.v;
                const c = audienceCounts[opt.v];
                return (
                  <button
                    type="button"
                    key={opt.v}
                    onClick={() => setForm({ ...form, audience: opt.v })}
                    className={`text-left p-3 border rounded-sm transition ${
                      active
                        ? "border-[color:var(--gold)] bg-[color:var(--gold)]/10"
                        : "border-white/10 hover:border-white/30"
                    }`}
                    data-testid={`blast-audience-${opt.v}`}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className="text-[11px] uppercase tracking-[0.22em]"
                        style={{ color: active ? "var(--gold)" : "rgba(255,255,255,0.75)" }}
                      >
                        {opt.l}
                      </span>
                      <span
                        className="text-[10px] tabular-nums font-semibold"
                        style={{ color: active ? "var(--gold)" : "rgba(255,255,255,0.5)" }}
                      >
                        {c ?? "·"}
                      </span>
                    </div>
                    <p className="text-[10px] text-white/45 mt-1 leading-snug">{opt.hint}</p>
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Body · plain text (double newline = paragraph break)" full>
            <Textarea
              required
              rows={8}
              minLength={10}
              maxLength={8000}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              className="bg-transparent border border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] resize-none"
              data-testid="blast-body"
            />
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="CTA Button Text (optional)">
              <Input
                value={form.cta_label}
                onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                className="bg-transparent border-0 border-b border-white/15 rounded-none h-11 px-0 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
                data-testid="blast-cta-label"
              />
            </Field>
            <Field label="CTA Button URL (optional)">
              <Input
                value={form.cta_url}
                onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
                className="bg-transparent border-0 border-b border-white/15 rounded-none h-11 px-0 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
                data-testid="blast-cta-url"
                placeholder="https://..."
              />
            </Field>
          </div>
          <Field label="Header Image URL (optional)" full>
            <Input
              value={form.image_url}
              onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              className="bg-transparent border-0 border-b border-white/15 rounded-none h-11 px-0 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
              data-testid="blast-image-url"
            />
          </Field>
          <div className="flex items-center gap-3 pt-3">
            <Button
              type="submit"
              disabled={busy}
              className="btn-gold rounded-none px-7 h-11 text-xs"
              data-testid="blast-save"
            >
              {busy ? "Saving…" : editingId ? "Update" : form.scheduled_for ? "Schedule" : "Save Draft"}
            </Button>
            <button
              type="button"
              onClick={reset}
              className="text-xs uppercase tracking-[0.22em] text-white/55 hover:text-white"
              data-testid="blast-cancel"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="space-y-3" data-testid="blasts-list">
        {items.length === 0 ? (
          <p className="text-sm text-white/55 py-4">No blasts yet. Compose one above.</p>
        ) : (
          items.map((a) => {
            const s = statusColors[a.status] || statusColors.draft;
            return (
              <div key={a.id} className="surface p-5 lg:p-6 space-y-3" data-testid={`blast-row-${a.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span
                        className="text-[10px] uppercase tracking-[0.25em] px-2 py-1 border"
                        style={{ color: s.color, borderColor: `${s.color}50` }}
                      >
                        {s.label}
                      </span>
                      {a.type_label && (
                        <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">
                          {a.type_label}
                        </span>
                      )}
                      <span
                        className="text-[10px] uppercase tracking-[0.2em] px-2 py-1 border border-white/10 rounded-sm text-white/55"
                        data-testid={`blast-audience-badge-${a.id}`}
                      >
                        → {AUDIENCE_OPTIONS.find((x) => x.v === (a.audience || "all"))?.l || "All Subscribers"}
                        {a.status !== "sent" && audienceCounts[a.audience || "all"] != null && (
                          <span className="ml-1.5 text-white/40">({audienceCounts[a.audience || "all"]})</span>
                        )}
                      </span>
                      {a.scheduled_for && a.status === "scheduled" && (
                        <span className="text-[10px] uppercase tracking-[0.2em] text-white/45">
                          {new Date(a.scheduled_for).toLocaleString()}
                        </span>
                      )}
                      {a.sent_at && (
                        <span className="text-[10px] uppercase tracking-[0.2em] text-white/45">
                          {new Date(a.sent_at).toLocaleString()} · {a.sent_count}/{a.audience_size || "?"} delivered
                        </span>
                      )}
                      {a.status === "sent" && (a.opens != null || a.clicks != null) && (
                        <span
                          className="text-[10px] uppercase tracking-[0.2em]"
                          style={{ color: "var(--gold)" }}
                          data-testid={`digest-stats-${a.id}`}
                        >
                          {a.opens || 0} opened
                          {a.sent_count > 0 && (
                            <span className="text-white/50">
                              {" "}({Math.round(((a.opens || 0) / a.sent_count) * 100)}%)
                            </span>
                          )}{" "}
                          · {a.clicks || 0} clicked
                          {a.sent_count > 0 && (
                            <span className="text-white/50">
                              {" "}({Math.round(((a.clicks || 0) / a.sent_count) * 100)}%)
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                    <h4 className="font-serif text-lg lg:text-xl tracking-tighter mb-1">
                      {a.title}
                    </h4>
                    <p className="text-sm text-white/55 line-clamp-2">{a.body}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {a.status !== "sent" && (
                      <>
                        <button
                          onClick={() => sendNow(a.id, a.title, a.audience)}
                          disabled={busy}
                          className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--gold)] hover:opacity-80"
                          data-testid={`blast-send-now-${a.id}`}
                        >
                          Send Now
                        </button>
                        <button
                          onClick={() => startEdit(a)}
                          className="text-[10px] uppercase tracking-[0.22em] text-white/55 hover:text-white"
                          data-testid={`blast-edit-${a.id}`}
                        >
                          <Edit3 size={11} className="inline mr-1" />
                          Edit
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => removeAnn(a.id)}
                      className="text-[10px] uppercase tracking-[0.22em] text-white/45 hover:text-red-400"
                      data-testid={`blast-delete-${a.id}`}
                    >
                      <Trash2 size={11} className="inline mr-1" />
                      Del
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
